import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import PreAuditValues from './PreAuditValues';
import {
  PreAuditValuesSectionProps,
  CallingAgentAuditDetails,
  AuditQuestionConfig,
  ApiAuditTrailAnswer
} from '../../interface';
import { AuditorType, QuestionTag } from '../auditEnums';

const PreAuditValuesSection = ({ readOnly }: PreAuditValuesSectionProps) => {
  const auditDetails = useSelector(
    (state: RootState) => state.callAudit?.callingAgentAuditDetails
  ) as CallingAgentAuditDetails | null;

  const auditQuestionnaire = (auditDetails?.auditConfig?.auditQuestionnaire ||
    []) as AuditQuestionConfig[];
  const auditTrail = auditDetails?.auditTrail || [];

  const dsAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.DS),
    [auditTrail]
  );
  const qcAgentAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.QC_AGENT),
    [auditTrail]
  );
  const qcTeamLeadAuditTrail = useMemo(
    () => auditTrail.find(t => t.auditor === AuditorType.QC_TEAM_LEAD),
    [auditTrail]
  );

  const violationReasonQuestion = useMemo(
    () => auditQuestionnaire.find(q => q.question_tag === QuestionTag.VIOLATION_REASON_LIST),
    [auditQuestionnaire]
  );
  const clearanceReasonQuestion = useMemo(
    () => auditQuestionnaire.find(q => q.question_tag === QuestionTag.CLEARANCE_REASON_LIST),
    [auditQuestionnaire]
  );

  const dsAnswers = (dsAuditTrail?.answers || []) as unknown as ApiAuditTrailAnswer[];
  const qcAgentAnswers = (qcAgentAuditTrail?.answers || []) as unknown as ApiAuditTrailAnswer[];
  const qcTeamLeadAnswers = (qcTeamLeadAuditTrail?.answers ||
    []) as unknown as ApiAuditTrailAnswer[];

  const getMultiValuesFromAnswers = (questionId: number, answersSource: ApiAuditTrailAnswer[]) => {
    const prefilledAnswer = answersSource.find(
      (a: ApiAuditTrailAnswer) => a.question_id === questionId
    );
    if (!prefilledAnswer) return [];
    const question = auditQuestionnaire.find(q => q.question_id === questionId);
    if (!question) return [];
    return prefilledAnswer.selected_option_ids
      .map((id: number) => {
        const option = question.options.find(opt => opt.option_id === id);
        return option ? { id: String(option.option_id), label: option.option_text } : null;
      })
      .filter(
        (v: { id: string; label: string } | null): v is { id: string; label: string } => v !== null
      );
  };

  const getAuditTrailMultiValues = (answersSource: ApiAuditTrailAnswer[]) => {
    if (violationReasonQuestion) {
      const values = getMultiValuesFromAnswers(violationReasonQuestion.question_id, answersSource);
      if (values.length > 0) return values;
    }
    if (clearanceReasonQuestion) {
      const values = getMultiValuesFromAnswers(clearanceReasonQuestion.question_id, answersSource);
      if (values.length > 0) return values;
    }
    return [];
  };

  const dsMultiValues = getAuditTrailMultiValues(dsAnswers);
  const qcAgentMultiValues = getAuditTrailMultiValues(qcAgentAnswers);
  const qcTeamLeadMultiValues = getAuditTrailMultiValues(qcTeamLeadAnswers);

  const hasDsMultiValues = dsMultiValues.length > 0;
  const hasQcAgentMultiValues = qcAgentMultiValues.length > 0;
  const hasQcTeamLeadMultiValues = qcTeamLeadMultiValues.length > 0;

  const hasAnyAuditorAfterDs = !!(qcAgentAuditTrail || qcTeamLeadAuditTrail);

  const showDsPreAuditValues =
    !readOnly && dsAuditTrail && hasAnyAuditorAfterDs && hasDsMultiValues;
  const showQcAgentPreAuditValues = readOnly
    ? !qcTeamLeadAuditTrail && qcAgentAuditTrail && hasQcAgentMultiValues
    : qcAgentAuditTrail && hasQcAgentMultiValues;
  const showQcTeamLeadPreAuditValues = readOnly
    ? qcTeamLeadAuditTrail && hasQcTeamLeadMultiValues
    : qcTeamLeadAuditTrail && hasQcTeamLeadMultiValues;

  return (
    <>
      {showDsPreAuditValues && (
        <PreAuditValues sourceOfValues={dsAuditTrail!.auditor} values={dsMultiValues} />
      )}
      {showQcAgentPreAuditValues && (
        <PreAuditValues sourceOfValues={qcAgentAuditTrail!.auditor} values={qcAgentMultiValues} />
      )}
      {showQcTeamLeadPreAuditValues && (
        <PreAuditValues
          sourceOfValues={qcTeamLeadAuditTrail!.auditor}
          values={qcTeamLeadMultiValues}
        />
      )}
    </>
  );
};

export default PreAuditValuesSection;
